import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/apiClient";
import { useSeo } from "../lib/useSeo";
import { PublicPageHeader, PublicPageFooter } from "./PublicPageChrome";

// Free tool: NC foreclosure upset bid calculator.
// NC §1-339.25: an upset bid must raise the standing bid by the greater of
// 5% or $750, the bidder deposits the greater of 5% of the upset bid or $750
// with the Clerk, and each qualifying upset bid restarts a 10-day window.
// Bid math stays client-side (it's arithmetic on the visible number). The
// 10-day DEADLINE is fetched from GET /api/v1/public/upset-deadline — the
// server knows the NC court holiday table (internal/courtcal), so the answer
// converges with the transitioner's and bid tracker's and stays right through
// Christmas clusters the old client-side weekend-only math got wrong by up to
// five days (#1638 item 4).
//
// Reuses PublicPageChrome + useSeo + the .legal-content styling.

// UpsetDeadlineResponse mirrors handlers.UpsetDeadlineResponse. Duplicated
// rather than shared through a generated types package because it's exactly
// four fields and the whole endpoint is one URL — the generated-types
// machinery would carry more code than it saves.
interface UpsetDeadlineResponse {
  bidDate: string;
  deadline: string;
  deadlineDisplay: string;
  holidaysApplied: boolean;
  estimate?: boolean;
  uncoveredYear?: number;
}

const usd = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  fontSize: "1rem",
  background: "var(--bg-card)",
  color: "var(--text-primary)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-md)",
  boxSizing: "border-box",
};

const UpsetBidCalculator: React.FC = () => {
  useSeo({
    title: "NC Upset Bid Calculator — Foreclosure Upset Bids | Parcely",
    description:
      "Free calculator for North Carolina foreclosure upset bids. Enter the current bid to get the minimum upset bid (5% or $750), the deposit, and the new 10-day deadline.",
  });

  const [currentBid, setCurrentBid] = useState("");
  const [lastBidDate, setLastBidDate] = useState("");
  const [deadlineInfo, setDeadlineInfo] = useState<UpsetDeadlineResponse | null>(null);
  const [deadlineError, setDeadlineError] = useState(false);

  const bid = parseFloat(currentBid);
  const valid = !isNaN(bid) && bid > 0;

  const increase = valid ? Math.max(bid * 0.05, 750) : 0;
  const minUpset = valid ? bid + increase : 0;
  const deposit = valid ? Math.max(minUpset * 0.05, 750) : 0;

  useEffect(() => {
    if (!lastBidDate) {
      setDeadlineInfo(null);
      setDeadlineError(false);
      return;
    }
    let cancelled = false;
    setDeadlineError(false);
    apiClient
      .get<UpsetDeadlineResponse>(`/public/upset-deadline?date=${encodeURIComponent(lastBidDate)}`)
      .then(({ data }) => {
        if (!cancelled) setDeadlineInfo(data);
      })
      .catch(() => {
        // Public tool — a bad user date is rare (the <input type="date"> pins
        // the format) and a transient network failure shouldn't turn a
        // deadline field into a spinner-of-death. Fall silent and let the
        // rest of the calculator still work; the caveat below the field
        // reminds the user to confirm with the Clerk regardless.
        if (!cancelled) {
          setDeadlineInfo(null);
          setDeadlineError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [lastBidDate]);

  const deadline = deadlineInfo?.deadlineDisplay ?? "";
  const isEstimate = deadlineInfo?.estimate === true;

  return (
    <>
      <PublicPageHeader />
      <div className="legal-page" id="main" tabIndex={-1}>
        <div className="legal-content">
          <h1>NC Upset Bid Calculator</h1>
          <p className="legal-updated">Foreclosure upset bids in North Carolina</p>

          <p>
            After a North Carolina foreclosure or tax sale, the high bid isn't final.
            State law (N.C.G.S. §1-339.25) opens a 10-day window where anyone can{" "}
            <strong>upset the bid</strong> by raising it enough and posting a deposit —
            and each upset restarts the clock. Enter the current bid below to see what it
            takes to upset it.
          </p>

          <div
            style={{
              background: "var(--bg-card)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)",
              padding: "20px",
              margin: "20px 0",
            }}
          >
            <label style={{ display: "block", marginBottom: 6, color: "var(--text-secondary)" }}>
              Current (standing) bid
            </label>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              placeholder="e.g. 11000"
              value={currentBid}
              onChange={(e) => setCurrentBid(e.target.value)}
              style={inputStyle}
              aria-label="Current standing bid in dollars"
            />

            <label style={{ display: "block", margin: "16px 0 6px", color: "var(--text-secondary)" }}>
              Date of the current bid <span style={{ color: "var(--text-muted)" }}>(optional)</span>
            </label>
            <input
              type="date"
              value={lastBidDate}
              onChange={(e) => setLastBidDate(e.target.value)}
              style={inputStyle}
              aria-label="Date of the current bid"
            />

            {valid && (
              <div style={{ marginTop: 20, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
                <div style={{ color: "var(--text-secondary)", fontSize: "var(--text-sm)" }}>
                  Minimum upset bid
                </div>
                <div style={{ fontSize: "2rem", fontWeight: 700, color: "var(--accent-blue)", lineHeight: 1.2 }}>
                  {usd(minUpset)}
                </div>
                <div style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)", marginBottom: 14 }}>
                  raises the bid by {usd(increase)} ({increase === 750 ? "the $750 floor — 5% was less" : "5% of the current bid"})
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
                  <div>
                    <div style={{ color: "var(--text-secondary)", fontSize: "var(--text-sm)" }}>
                      Deposit with the Clerk
                    </div>
                    <div style={{ fontSize: "1.25rem", fontWeight: 600 }}>{usd(deposit)}</div>
                    <div style={{ color: "var(--text-muted)", fontSize: "var(--text-xs)" }}>
                      greater of 5% or $750 — verify
                    </div>
                  </div>
                  {deadline && (
                    <div>
                      <div style={{ color: "var(--text-secondary)", fontSize: "var(--text-sm)" }}>
                        New 10-day deadline
                      </div>
                      <div style={{ fontSize: "1.25rem", fontWeight: 600 }}>{deadline}</div>
                      <div style={{ color: "var(--text-muted)", fontSize: "var(--text-xs)" }}>
                        {isEstimate
                          ? `estimated — our NC court holiday table doesn't cover ${
                              deadlineInfo?.uncoveredYear ?? "that year"
                            } yet, so the answer may be earlier than the true statutory close`
                          : "rolls past weekends and NC court holidays to the next business day"}
                      </div>
                    </div>
                  )}
                  {!deadline && lastBidDate && deadlineError && (
                    <div>
                      <div style={{ color: "var(--text-secondary)", fontSize: "var(--text-sm)" }}>
                        New 10-day deadline
                      </div>
                      <div style={{ color: "var(--text-muted)", fontSize: "var(--text-xs)" }}>
                        Couldn't reach the deadline calculator right now — the disclaimer below still stands.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <h2>How the upset bid works</h2>
          <p>
            To upset a bid, you raise the standing bid by the <strong>greater of 5% or
            $750</strong>, then deposit the greater of 5% of your new bid or $750 with the
            Clerk of Superior Court. Filing a valid upset bid starts a fresh{" "}
            <strong>10-day period</strong>, so a property can stay open for weeks as bidders
            keep raising each other. When 10 days pass with no new upset, the sale is
            confirmed and the deed is delivered. For the full process, see our{" "}
            <a href="/mecklenburg-tax-foreclosures">NC tax-foreclosure guide</a>.
          </p>

          <p>
            <a
              href="/public"
              className="btn-primary"
              style={{ display: "inline-block", textDecoration: "none", marginTop: "0.5rem" }}
            >
              Track open upset windows on Parcely →
            </a>
          </p>

          <hr className="legal-divider" />
          <p>
            <strong>This calculator is general information, not legal or financial advice.</strong>{" "}
            Bid increments, deposit amounts, and deadline counting are governed by statute and
            local practice and can change. Confirm every figure with the Clerk of Superior
            Court in the county where the sale is held before you act.
          </p>
          <p className="legal-entity">
            <strong>Parcely LLC</strong>
            <br />
            Charlotte, NC
          </p>
        </div>
      </div>
      <PublicPageFooter />
    </>
  );
};

export default UpsetBidCalculator;
